class BundleBuilder extends HTMLElement {
  constructor() {
    super();

    this.selectedVariants = [];
    this.observeResize();
    this.swiper = new Swiper(this, {
      allowTouchMove: false,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      on: {
        beforeSlideChangeStart: () => {
          this.onSlideChange();
        }
      }
    });
    this.initializeQuantityInputs();
    this.onBeginBuilder('.splash button');
    this.onClickVariant('.variants__grid__cell');
    this.onClickNextStep('.step-controls__next');
    this.onClickPreviousStep('.step-controls__prev');
    this.subtotalLabels = this.querySelectorAll('span[data-total]');
    this.successResetButton = this.querySelector(
      '.success-step .step-controls .button'
    );

    if (this.successResetButton) {
      this.successResetButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.resetBundle();
      });
    }
  }

  /**
   * hook up bundle builder quantity selectors
   * and call handleQuantityChange on each change.
   */
  initializeQuantityInputs() {
    const quantityInputs = this.querySelectorAll(
      '.bundle-builder__quantity-input'
    );
    quantityInputs.forEach((wrapper) => {
      const decrease = wrapper.querySelector(
        '.bundle-builder__quantity-input__button.decrease'
      );
      const increase = wrapper.querySelector(
        '.bundle-builder__quantity-input__button.increase'
      );
      const input = wrapper.querySelector(
        '.bundle-builder__quantity-input__input'
      );
      const cell = wrapper.closest('.variants__grid__cell');
      if (!cell || !input) {
        return;
      }

      // seed data-quantity
      const initial = parseInt(input.value, 10) || 0;
      cell.dataset.quantity = initial;

      const clampAndNotify = () => {
        let qty = parseInt(input.value, 10) || 0;
        const min = parseInt(input.min, 10) || 0;
        const max = parseInt(input.max, 10) || Infinity;
        qty = Math.min(Math.max(qty, min), max);
        input.value = qty;
        this.handleQuantityChange(cell, qty);
      };

      decrease.addEventListener('click', (e) => {
        e.stopPropagation();
        if (parseInt(input.value, 10) > parseInt(input.min, 10)) {
          input.value = parseInt(input.value, 10) - 1;
          clampAndNotify();
        }
      });

      increase.addEventListener('click', (e) => {
        e.stopPropagation();
        if (parseInt(input.value, 10) < parseInt(input.max, 10)) {
          input.value = parseInt(input.value, 10) + 1;
          clampAndNotify();
        }
      });
    });
  }

  /**
   * Called whenever a .quantity-input__input changes.
   * @param {HTMLElement} cell   the .variants__grid__cell
   * @param {number}      qty    new quantity
   */
  handleQuantityChange(cell, qty) {
    const slide = cell.closest('.swiper-slide');
    cell.dataset.quantity = qty;

    if (qty === 0) {
      this.removeFromBundle(cell);
    }

    // update your subtotal display
    this.updateCurrentStep(slide);
    this.updateBundleSubtotal();
  }

  /**
   * Scroll variant containers to the top before each slide change.
   */
  onSlideChange() {
    const variantContainers = this.querySelectorAll('.swiper-slide .variants');
    variantContainers.forEach((container) => {
      container.scrollTo(0, 0);
    });
  }

  /**
   * Set the variant’s data-quantity attribute and sync its input value.
   * @param {HTMLElement} variant
   * @param {number} qty
   */
  setVariantQuantity(variant, qty) {
    variant.setAttribute('data-quantity', qty);
    const input = variant.querySelector(
      '.bundle-builder__quantity-input__input'
    );
    if (input) {
      input.value = qty;
    }
  }

  /**
   * Adds a variant to the bundle.
   * @param {HTMLElement} variant
   */
  addToBundle(variant) {
    const variantIndex = this.findVariantInBundle(variant);

    if (variantIndex > -1) {
      return;
    }

    variant.setAttribute('aria-selected', 'true');
    if (variant.hasAttribute('data-quantity')) {
      this.setVariantQuantity(variant, 1);
    }
    this.selectedVariants.push(variant);
  }

  /**
   * Removes a variant from the bundle.
   * @param {HTMLElement} variant
   */
  removeFromBundle(variant) {
    const variantIndex = this.findVariantInBundle(variant);

    if (variantIndex < 0) {
      return;
    }

    variant.setAttribute('aria-selected', 'false');
    if (variant.hasAttribute('data-quantity')) {
      this.setVariantQuantity(variant, 0);
    }
    this.selectedVariants.splice(variantIndex, 1);
  }

  /**
   * Returns the index of the first found variant for a specific step.
   * @param {HTMLElement} variant
   * @returns {number}
   */
  findVariantInBundle(variant) {
    return this.selectedVariants.findIndex((element) => {
      return (
        element.dataset.id === variant.dataset.id &&
        element.dataset.step === variant.dataset.step
      );
    });
  }

  /**
   * Bundle builder takes up the remaining viewport on mobile by determining
   * the height of the header and promo bar first.
   */
  observeResize() {
    let header = document.getElementById('shopify-section-header');

    if (!header) {
      header = document.getElementById('shopify-section-header--mobile-first');
    }

    if (!header) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height;
      this.style.setProperty('--header-height', `${height}px`);
    });

    observer.observe(header);
  }

  /**
   * Begins the bundle builder when clicking the splash screen's button.
   * @param {string} selector
   */
  onBeginBuilder(selector) {
    const button = this.querySelector(selector);

    if (!button) {
      // Splash screen is optional.
      return;
    }

    button.addEventListener('click', (e) => {
      e.preventDefault();
      this.swiper.slideNext();
    });
  }

  /**
   * Updates the state of a variant displayed within a step's grid.
   * @param {string} selector
   */
  onClickVariant(selector) {
    const callback = (e) => {
      if (
        e.target.dataset.modalBundleItem ||
        e.target.closest('.bundle-builder__quantity-input')
      ) {
        return;
      }

      const variant = e.target.closest(selector);

      if (!variant || variant.classList.contains('sold-out')) {
        return;
      }

      const multiSelectableStep = variant.closest(
        '[aria-multiselectable="true"]'
      );
      // Get attributes to support Firefox.
      const ariaDisabled = variant.getAttribute('aria-disabled');
      const ariaSelected = variant.getAttribute('aria-selected');

      if (ariaDisabled === 'true' && multiSelectableStep) {
        // Selection limit reached.
        return;
      }

      if (ariaDisabled === 'true' && !multiSelectableStep) {
        // Replace the previously selected variant for this one.
        const previousVariant = variant.parentElement.querySelector(
          '[aria-selected="true"]'
        );
        if (!previousVariant) {
          return;
        }
        this.removeFromBundle(previousVariant);
        variant.setAttribute('aria-disabled', 'false');
      }

      e.preventDefault();

      if (ariaSelected === 'false') {
        this.addToBundle(variant);
      } else {
        this.removeFromBundle(variant);
      }

      this.updateCurrentStep(variant.closest('.swiper-slide'));
      this.updateBundleSubtotal();
    };

    this.addEventListener('click', callback);
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') {
        return;
      }

      if (e.repeat) {
        return;
      }

      callback(e);
    });
  }

  /**
   * Updates the current builder step. Some products may get disabled, enabled,
   * and the total bundle pricing may get updated.
   * @param {HTMLElement} slide
   */
  updateCurrentStep(slide) {
    if (!slide) {
      return;
    }

    const requiredChoices = parseInt(slide.dataset.requiredChoices, 10);
    const selectedVariants = slide.querySelectorAll('[aria-selected="true"]');
    const nextStepButton = slide.querySelector('.step-controls__next');

    let currentCount = selectedVariants.length;
    if (slide.hasAttribute('data-quantity-selector')) {
      currentCount = 0;
      slide.querySelectorAll('.variants__grid__cell').forEach((cell) => {
        const qty = parseInt(cell.dataset.quantity, 10) || 0;
        currentCount += qty;
      });
    }

    if (currentCount === requiredChoices) {
      // With a completed selection all other options are now disabled.
      const unselectedVariants = slide.querySelectorAll(
        '[aria-selected="false"]:not(.sold-out)'
      );
      unselectedVariants.forEach((variant) => {
        variant.setAttribute('aria-disabled', 'true');
      });
      const selectedVariants = slide.querySelectorAll(
        '[aria-selected="true"]:not(.sold-out)'
      );
      selectedVariants.forEach((variant) => {
        const quantityInput = variant.querySelector(
          '.bundle-builder__quantity-input'
        );
        if (quantityInput) {
          quantityInput.querySelector('.increase').disabled = true;
        }
      });
      slide.dataset.stepComplete = true;
      nextStepButton.disabled = false;
      return;
    }

    selectedVariants.forEach((variant) => {
      const container = variant.querySelector(
        '.bundle-builder__quantity-input'
      );
      if (!container) {
        return;
      }

      const inputBtn = container.querySelector('.increase');
      const inputField = container.querySelector('input');
      const currentValue = parseInt(inputField.value, 10) || 0;
      const maxValue = parseInt(inputField.max, 10) || Infinity;

      // only enable if we haven’t yet hit the max
      inputBtn.disabled = currentValue >= maxValue;
    });
    const disabledVariants = slide.querySelectorAll(
      '[aria-disabled="true"]:not(.sold-out)'
    );
    disabledVariants.forEach((variant) => {
      variant.setAttribute('aria-disabled', 'false');
    });
    slide.dataset.stepComplete = false;
    nextStepButton.disabled = true;
  }

  /**
   * Progresses the bundle builder to the next step/slide.
   * @param {string} selector
   */
  onClickNextStep(selector) {
    this.addEventListener('click', (e) => {
      const button = e.target.closest(selector);

      if (!button) {
        return;
      }

      e.preventDefault();

      if (button.dataset.createBundle === 'true') {
        this.createBundle();
        return;
      }

      this.swiper.slideNext();
    });
  }

  /**
   * Returns to the previous step/slide.
   * @param {string} selector
   */
  onClickPreviousStep(selector) {
    this.addEventListener('click', (e) => {
      if (!e.target.closest(selector)) {
        return;
      }
      e.preventDefault();
      this.swiper.slidePrev();
    });
  }

  /**
   * Calculates the bundle's subtotal then updates the user interface.
   */
  updateBundleSubtotal() {
    const subtotal = this.selectedVariants.reduce((sum, variant) => {
      const price = parseFloat(variant.dataset.price, 10) || 0;
      const qty =
        variant.dataset.quantity !== undefined
          ? parseInt(variant.dataset.quantity, 10) || 0
          : 1;
      return sum + price * qty;
    }, 0);

    this.dataset.subtotal = subtotal;

    this.subtotalLabels.forEach((label) => {
      label.innerText = theme.utils.toMoney(subtotal);
    });
  }

  getItemJSON(id, bundleGroupId, step, quantity) {
    return {
      id,
      quantity: quantity,
      properties: {
        _bundle_id: this.dataset.handle,
        _bundle_group_id: bundleGroupId,
        _bundle_title: this.dataset.title,
        _bundle_image: this.dataset.image,
        _bundle_qty: quantity,
        _bundle_step: step
      }
    };
  }

  /**
   * Creates the bundle, adds it to the cart, then redirects
   * the user to the cart page.
   */
  createBundle() {
    if (this.dataset.appBundle === 'true') {
      this.createAppBundle();
      return;
    }

    const bundleGroupId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const parentVariant = parseInt(this.dataset.variant);
    let variants = this.selectedVariants
      .filter((item) => !item.classList.contains('optional'))
      .map((item) => {
        const id = parseInt(item.dataset.id, 10);
        const noShip = item.dataset.noShip;
        const step = parseInt(item.dataset.step, 10);
        const quantity = parseInt(item.dataset.quantity, 10) || 1;
        const itemToAdd = this.getItemJSON(id, bundleGroupId, step, quantity);

        if (noShip) {
          itemToAdd.properties._noship = noShip;
        }

        return itemToAdd;
      });

    if (parentVariant) {
      variants.push(this.getItemJSON(parentVariant, bundleGroupId, 0, 1));
    }

    theme.cart.add(variants).then(() => {
      if (this.successResetButton) {
        this.swiper.slideNext();
      }
    });
  }

  /**
   * Creates an app-based dynamic bundle. Adds only the selected component
   * variants as individual cart lines (no parent line). Each component
   * carries bundle identity via line properties. A cart transform Shopify
   * Function merges them into a single bundle display.
   *
   * Component line properties (_ prefixed = hidden from storefront cart):
   *   _bundle_component: "true"
   *   _bundle_group_id: unique timestamp ID linking components
   *   _bundle_step: "step-N" matching the rules step key
   *   _bundle_title: display title for the merged bundle (every component)
   *   _bundle_image: parent variant image URL (every component)
   *   _bundle_url: parent product URL (every component)
   *   _bundle_parent_variant_id: GID of the parent variant (first component only)
   *   _bundle_rules: JSON string of bundle rules (first component only)
   */
  createAppBundle() {
    const parentVariantId = `gid://shopify/ProductVariant/${this.dataset.variant}`;
    const bundleGroupId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const bundleTitle = this.dataset.title;
    const bundleImage = this.dataset.image;
    const bundleUrl = this.dataset.url;
    const bundleRules = this.dataset.rules;

    const items = [];
    let isFirst = true;

    this.selectedVariants
      .filter((item) => !item.classList.contains('optional'))
      .forEach((item) => {
        const id = parseInt(item.dataset.id, 10);
        const step = parseInt(item.dataset.step, 10);
        const quantity = parseInt(item.dataset.quantity, 10) || 1;

        const properties = {
          _bundle_component: 'true',
          _bundle_group_id: bundleGroupId,
          _bundle_step: `step-${step}`,
          _bundle_title: bundleTitle,
          _bundle_image: bundleImage,
          _bundle_url: bundleUrl
        };

        if (isFirst) {
          properties._bundle_parent_variant_id = parentVariantId;
          properties._bundle_rules = bundleRules;
          isFirst = false;
        }

        items.push({ id, quantity, properties });
      });

    theme.cart.add(items).then(() => {
      if (this.successResetButton) {
        this.swiper.slideNext();
      }
    });
  }

  /**
   * Reset the bundle builder to its initial state.
   */
  resetBundle() {
    this.selectedVariants = [];
    this.querySelectorAll('[aria-selected="true"]').forEach((element) => {
      element.setAttribute('aria-selected', 'false');
    });
    this.querySelectorAll('[aria-disabled="true"]').forEach((element) => {
      element.setAttribute('aria-disabled', 'false');
    });
    this.querySelectorAll('[data-quantity]').forEach((el) => {
      el.setAttribute('data-quantity', '0');
    });
    this.updateBundleSubtotal();
    this.swiper.slideTo(1);
  }
}

customElements.define('bundle-builder', BundleBuilder);
